import { useEffect, useMemo, useState } from "react";
import { observer } from "mobx-react-lite";
import { useDebounce } from "use-debounce";
import { ChevronDown, ChevronUp, Loader2, Info } from "lucide-react";
import { DataTable } from "@/components/common/DataTable";
import { Badge } from "@/components/ui/badge";
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { useTran } from "@/hooks";
import { formatDateTime } from "@/lib/utils/date";
import {
  useOverviewStore,
  useDataEntryFiltersStore,
} from "../../hooks/useDataEntryStore";
import { useOrgPathById } from "../../hooks/useOrgPathById";
import { loadOverview } from "../../operations/overview.ops";
import { formatMetricValue } from "../../domains/dataEntry";
import { humanizeFormula } from "@/features/esg/utils/formula-display";
import CategoryBadge from "../common/CategoryBadge";
import ReportingPeriodLabel from "../common/ReportingPeriodLabel";
import SourceCell from "../common/SourceCell";
import { reportingYearStore } from "@/features/reporting-boundary/stores/reporting-year.store";
import OrgStatusTag from "@/features/organization/components/OrgStatusTag";
import { useEntryDataStore } from "../../hooks/useDataEntryStore";
import {
  buildOrgStatusInfoMap,
  findOrgPathWithStatus,
} from "@/components/common/organization.utils";

// Splits formulaValues keys into baseline-anchored refs and current-year refs.
// Returns { baselineEntries: [{code, value, yearLabel}], currentEntries: [...] }
const splitFormulaValues = (formulaValues, baselineYear, reportingDate) => {
  if (!formulaValues) return { baselineEntries: [], currentEntries: [] };
  const baselineEntries = [];
  const currentEntries = [];
  const yearLabel = baselineYear ? String(baselineYear) : "baseline";
  const currentYear = reportingDate
    ? new Date(reportingDate).getFullYear().toString()
    : "";

  for (const [key, val] of Object.entries(formulaValues)) {
    if (key.includes("@baseline")) {
      const code = key.replace("@baseline", "");
      baselineEntries.push({ code, value: val, yearLabel });
    } else {
      currentEntries.push({
        code: key,
        value: val,
        yearLabel: currentYear,
      });
    }
  }
  return { baselineEntries, currentEntries };
};

const DepValueRow = ({ code, value, yearLabel }) => (
  <div className="flex items-center justify-between gap-4 py-0.5">
    <span className="text-xs text-muted-foreground">
      {code}
      {yearLabel && (
        <span className="text-muted-foreground/70 ml-1">({yearLabel})</span>
      )}
    </span>
    {value == null ? (
      <span className="text-xs font-medium text-muted-foreground/60">n.a</span>
    ) : (
      <span className="text-xs font-semibold text-foreground tabular-nums">
        {formatMetricValue(value)}
      </span>
    )}
  </div>
);

const FormulaTooltipBody = ({
  formula,
  formulaValues,
  baselineYear,
  reportingDate,
  unit,
  displayValue,
}) => {
  const { t } = useTran();
  const { baselineEntries, currentEntries } = splitFormulaValues(
    formulaValues,
    baselineYear,
    reportingDate,
  );
  const hasBaseline = baselineEntries.length > 0;
  const hasCurrent = currentEntries.length > 0;

  return (
    <div className="space-y-2">
      {/* Formula */}
      <div className="space-y-1">
        <p className="text-xs font-semibold text-muted-foreground">
          {t("Formula")}
        </p>
        <p className="text-xs text-primary break-words leading-relaxed">
          {humanizeFormula(formula, { baselineYear })}
        </p>
      </div>

      {/* Baseline values */}
      {hasBaseline && (
        <div className="space-y-0.5">
          <p className="text-xs font-semibold text-muted-foreground">
            {baselineYear
              ? `${t("Baseline")} (${baselineYear})`
              : t("Baseline")}
          </p>
          {baselineEntries.map((e) => (
            <DepValueRow key={`b-${e.code}`} {...e} />
          ))}
        </div>
      )}

      {/* Current reporting year values */}
      {hasCurrent && (
        <div className="space-y-0.5">
          <p className="text-xs font-semibold text-muted-foreground">
            {t("Reporting Year")}
          </p>
          {currentEntries.map((e) => (
            <DepValueRow key={`c-${e.code}`} {...e} />
          ))}
        </div>
      )}

      {/* Result */}
      <div className="pt-2 mt-1 border-t border-border">
        <div className="flex items-center justify-between gap-6 px-3 py-2 rounded-md border border-primary/20 bg-primary/5">
          <span className="text-sm font-semibold text-muted-foreground">
            {t("Result")}
          </span>
          <span className="text-sm font-bold text-foreground tabular-nums">
            = {displayValue}
            {unit ? ` ${unit}` : ""}
          </span>
        </div>
      </div>
    </div>
  );
};

const OverviewTable = observer(() => {
  const { t } = useTran();
  const {
    items,
    total,
    loading,
    page,
    pageSize,
    sort,
    setPage,
    setPageSize,
    setSort,
  } = useOverviewStore();
  const sharedFilters = useDataEntryFiltersStore().filters;
  const { orgTree } = useEntryDataStore();
  const orgPathById = useOrgPathById();
  const orgStatusMap = useMemo(() => buildOrgStatusInfoMap(orgTree), [orgTree]);

  const [expandedPaths, setExpandedPaths] = useState(() => new Set());
  const togglePath = (key) => {
    setExpandedPaths((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const [debouncedSearch] = useDebounce(sharedFilters.search, 400);
  const orgIdsKey = (sharedFilters.orgIds || []).join(",");

  const { reportingYears } = reportingYearStore;

  useEffect(() => {
    loadOverview();
  }, [
    page,
    pageSize,
    sort.key,
    sort.direction,
    sharedFilters.year,
    sharedFilters.framework,
    sharedFilters.category,
    sharedFilters.metricType,
    orgIdsKey,
    debouncedSearch,
  ]);

  // Reset to page 1 when any filter that can shrink `total` changes —
  // otherwise the server's slice for the stale page returns empty rows
  // while pagination's clamp masks the drift.
  useEffect(() => {
    setPage(1);
  }, [
    debouncedSearch,
    sharedFilters.year,
    sharedFilters.framework,
    sharedFilters.category,
    sharedFilters.metricType,
    orgIdsKey,
    setPage,
  ]);

  const columns = useMemo(
    () => [
      {
        key: "index",
        header: "#",
        width: "56px",
        render: (_row, i) => (page - 1) * pageSize + i + 1,
      },
      {
        key: "disclosure",
        header: t("Metrics"),
        sortable: true,
        sortKey: "disclosureCode",
        showSortIndicators: true,
        width: "300px",
        render: (row) => (
          <div className="flex items-center gap-1.5 min-w-0">
            <Badge variant="outline" className="font-mono text-xs shrink-0">
              {row.disclosureCode}
            </Badge>
            <span className="truncate" title={row.disclosureTitle}>
              {row.disclosureTitle}
            </span>
          </div>
        ),
      },
      {
        key: "category",
        header: t("Category"),
        width: "140px",
        render: (row) => <CategoryBadge category={row.category} />,
      },
      {
        key: "organizationName",
        header: t("Organization"),
        sortable: true,
        sortKey: "organizationName",
        showSortIndicators: true,
        width: "320px",
        render: (row) => {
          const pathWithStatus = findOrgPathWithStatus(orgTree, row.orgId);
          const path = orgPathById.get(String(row.orgId || "")) || "";
          const rowKey =
            row._id || `${row.disclosureId}-${row.orgId}-${row.reportingDate}`;
          const expanded = expandedPaths.has(rowKey);
          // status/inactiveDate come from the path when available, else fall
          // back to the id→{status,inactiveDate} map (buildOrgStatusInfoMap).
          const statusInfo =
            pathWithStatus.length > 0
              ? pathWithStatus[pathWithStatus.length - 1]
              : orgStatusMap.get(String(row.orgId || ""));
          const status = statusInfo?.status;
          const inactiveDate = statusInfo?.inactiveDate;
          return (
            <div className="min-w-0">
              <div className="flex items-center gap-1 truncate text-sm">
                <span className="truncate">{row.organizationName || "—"}</span>
                {row.orgId && (
                  <OrgStatusTag
                    status={status}
                    inactiveDate={inactiveDate}
                    className="text-[10px] shrink-0"
                  />
                )}
              </div>
              {path && (
                <button
                  type="button"
                  onClick={() => togglePath(rowKey)}
                  className="group flex w-full items-start gap-1 text-left text-xs text-muted-foreground hover:text-foreground transition-colors"
                  title={
                    expanded
                      ? t("Click to collapse")
                      : t("Click to show full path")
                  }
                >
                  {expanded && pathWithStatus.length > 0 ? (
                    <span className="min-w-0 flex-1 whitespace-normal break-words">
                      <span className="inline-flex flex-wrap items-center gap-0.5">
                        {pathWithStatus.map((node, idx) => {
                          const isLast = idx === pathWithStatus.length - 1;
                          return (
                            <span
                              key={`${node.id}-${idx}`}
                              className="inline-flex items-center gap-0.5"
                            >
                              {idx > 0 && (
                                <span className="px-0.5 text-muted-foreground/50">
                                  ›
                                </span>
                              )}
                              <span
                                className={
                                  isLast
                                    ? "text-primary font-medium"
                                    : "text-muted-foreground"
                                }
                              >
                                {node.name}
                              </span>
                              <OrgStatusTag
                                status={node.status}
                                inactiveDate={node.inactiveDate}
                                className="text-[10px]"
                              />
                            </span>
                          );
                        })}
                      </span>
                    </span>
                  ) : (
                    <span className="min-w-0 flex-1 truncate">
                      {pathWithStatus.length > 0
                        ? pathWithStatus.map((node) => node.name).join(" › ")
                        : `${path} > ${row.organizationName}`}
                    </span>
                  )}
                  {expanded ? (
                    <ChevronUp size={12} className="mt-0.5 shrink-0" />
                  ) : (
                    <ChevronDown size={12} className="mt-0.5 shrink-0" />
                  )}
                </button>
              )}
            </div>
          );
        },
      },
      {
        key: "reportingDate",
        header: t("Reporting Year"),
        sortable: true,
        sortKey: "reportingDate",
        showSortIndicators: true,
        width: "1200px",
        render: (row) => {
          if (!row.reportingDate) return "—";

          return (
            <ReportingPeriodLabel
              reportingYears={reportingYears}
              entryValue={row.reportingDate}
              row={row}
            />
          );
        },
      },
      {
        key: "value",
        header: t("Result"),
        sortable: true,
        sortKey: "value",
        showSortIndicators: true,
        width: "260px",
        render: (row) => {
          // Prefer the `after` column when present (post-change value).
          const raw =
            row.after != null && row.after !== "" ? row.after : row.value;
          if (raw == null || raw === "") {
            if (row.type === "sub_entry_aggregate") return "n.a";
            return "n.a";
          }

          const displayValue =
            row.unit === "Year" ? String(raw) : formatMetricValue(raw);
          return (
            <div className="flex items-center gap-1 min-w-0">
              <span className="tabular-nums truncate">{displayValue}</span>
              {row.unit && (
                <span className="text-muted-foreground text-xs shrink-0">
                  {row.unit}
                </span>
              )}
              {row.formula && (
                <TooltipProvider delayDuration={150}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span
                        role="button"
                        tabIndex={0}
                        className="inline-flex cursor-pointer text-muted-foreground hover:text-foreground outline-none shrink-0"
                      >
                        <Info className="h-3.5 w-3.5" />
                      </span>
                    </TooltipTrigger>
                    <TooltipContent
                      side="top"
                      align="center"
                      sideOffset={6}
                      className="max-w-xs"
                    >
                      <FormulaTooltipBody
                        formula={row.formula}
                        formulaValues={row.formulaValues}
                        baselineYear={row.baselineYear}
                        reportingDate={row.reportingDate}
                        unit={row.unit}
                        displayValue={displayValue}
                      />
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
            </div>
          );
        },
      },
      {
        key: "source",
        header: t("Metric Type"),
        width: "120px",
        render: (row) => <SourceCell source={row.source} type={row.type} />,
      },
      {
        key: "createdAt",
        header: t("Created Date"),
        sortable: true,
        sortKey: "createdAt",
        showSortIndicators: true,
        width: "140px",
        render: (row) =>
          row.createdAt
            ? formatDateTime(row.createdAt, { timeStyle: undefined })
            : "—",
      },
    ],
    [page, pageSize, t, orgPathById, expandedPaths, reportingYears, orgTree],
  );

  const handleSort = (key) => {
    const direction =
      sort.key === key && sort.direction === "asc" ? "desc" : "asc";
    setSort({ key, direction });
  };

  // Skeleton only on initial load. Filter refetches dim the existing rows
  // via opacity instead of swapping in skeletons mid-flow.
  const initialLoad = loading && items.length === 0;
  const refetching = loading && items.length > 0;

  return (
    <div className="relative">
      {refetching && (
        <div className="pb-3 pointer-events-none sticky top-2 z-10 flex justify-center">
          <div className="rounded-full bg-background shadow-md ring-1 ring-border px-3 py-1.5 flex items-center gap-2 text-xs text-muted-foreground">
            <Loader2 className="h-3.5 w-3.5 animate-spin text-primary" />
            <span>{t("Refreshing...")}</span>
          </div>
        </div>
      )}
      <div
        className={cn(
          "overflow-x-auto transition-opacity",
          refetching && "opacity-60",
        )}
      >
        <DataTable
          columns={columns}
          data={items}
          loading={initialLoad}
          sort={sort}
          onSort={handleSort}
          emptyMessage={t("No data entries found.")}
          getRowKey={(row, i) =>
            row._id || `computed-${row.disclosureId}-${row.orgId}-${i}`
          }
          pagination={{
            page,
            pageSize,
            total,
            onPageChange: setPage,
            onPageSizeChange: (size) => setPageSize(size),
            pageSizeOptions: [10, 20, 50],
            disabled: loading,
          }}
        />
      </div>
    </div>
  );
});

export default OverviewTable;
